import { ModulePermission, PermissionCode, User } from "@/lib/types";
import { TEAM_DEPT_KEY, TEAM_PROFILE_KEY, TEAM_PROFILE_TABS, TEAM_ROSTER_KEY, tabPermKey, tabTopToModules } from "@/lib/dept-tabs";

// У вкладки есть только просмотр/правка/удаление, а у модулей действий было восемь.
// Решение Олега 10.08.2026 («модулей нет, есть отделы и вкладки»): кто может править
// вкладку — тот внутри неё публикует, настраивает и создаёт. `export` не проверяется
// нигде и просто перестаёт что-либо значить.
// ⚠️ KEEP IN SYNC с _ACTION_FOLD в backend/app/core/perm_rules.py.
const ACTION_FOLD: Partial<Record<PermissionCode, PermissionCode>> = {
  publish: "edit",
  settings: "edit",
  create: "edit",
  manage_users: "edit",
};

export function foldAction(action: PermissionCode): PermissionCode {
  return ACTION_FOLD[action] ?? action;
}

// Доступ к модулю. Источник прав ОДИН — вкладки: module-права (ключ без префикса
// `tab:`) больше не признаются, они раздавали доступ к разделам, которых человек
// в интерфейсе не видел. Перенос сохранённых записей — миграция на бэкенде
// (`_migrate_module_perms_to_tabs`).
export function hasPermission(user: User | null, moduleKey: string, action: PermissionCode) {
  if (!user) return false;
  if (user.role === "platform_admin") return true;
  const want = foldAction(action);
  return user.permissions.some((p) => {
    if (!p.moduleKey.startsWith("tab:")) return false;
    const parts = p.moduleKey.split(":"); // tab:<dept>:<top>[:<child>]
    return Boolean(parts[1] && parts[2]) && tabTopToModules(parts[1], parts[2]).includes(moduleKey) && p.allowed.includes(want);
  });
}

// Есть ли у человека права по вкладкам этого отдела. Раньше отсюда включался режим
// вкладок (иначе гейтинг шёл по модулям); теперь режим один, а функция осталась —
// по ней отдел решает, показывать ли вообще что-то, кроме пустого состояния.
export function deptUsesTabPerms(user: User | null, deptKey: string): boolean {
  if (!user) return false;
  return user.permissions.some((p) => p.moduleKey.startsWith(`tab:${deptKey}:`));
}

// Доступ к конкретной вкладке (top или top+child) по действию action (по умолчанию view).
export function canViewTab(user: User | null, deptKey: string, topKey: string, childKey?: string, action: PermissionCode = "view"): boolean {
  if (!user) return false;
  if (user.role === "platform_admin") return true;
  const key = tabPermKey(deptKey, topKey, childKey);
  const want = foldAction(action);
  return user.permissions.some((p) => p.moduleKey === key && p.allowed.includes(want));
}

// ── Команда: список и карточка сотрудника ───────────────────────────────────
// Раздел верхнего уровня, но права у него как у отдела: ключи tab:team:*.
// ⚠️ СВОЯ КАРТОЧКА ОТКРЫТА ВСЕГДА. Иначе человек не посмотрит собственный
// отпуск, документы и расчётный лист — а это его же данные (Максим, 04.09.2026).

export function canTeamRoster(user: User | null): boolean {
  return canViewTab(user, TEAM_DEPT_KEY, TEAM_ROSTER_KEY);
}

/** Может ли открыть карточку сотрудника: свою — всегда, чужую — по праву.
 *
 *  ⚠️ ДОСТАТОЧНО ПРАВА НА ЛЮБУЮ ВКЛАДКУ. Право «Карточка сотрудника» открывает
 *  её целиком, но выдать одну только «Оценку» тоже должно работать: иначе
 *  такое право ничего не даёт — человек упирается в закрытую карточку и до
 *  своей вкладки не доходит (проверено на деве 04.09.2026). */
export function canOpenProfile(user: User | null, ownerUserId?: string | null): boolean {
  if (!user) return false;
  if (ownerUserId && ownerUserId === user.id) return true;
  if (canViewTab(user, TEAM_DEPT_KEY, TEAM_PROFILE_KEY)) return true;
  return TEAM_PROFILE_TABS.some((t) => canViewTab(user, TEAM_DEPT_KEY, TEAM_PROFILE_KEY, t.key));
}

/** Видна ли вкладка карточки. В своей карточке открыты все, кроме служебных:
 *  «Доступы и роли» и «Настройки» — это управление человеком, а не его данные. */
export function canProfileTab(user: User | null, tab: string, ownerUserId?: string | null): boolean {
  if (!user) return false;
  if (user.role === "platform_admin") return true;
  const своя = Boolean(ownerUserId && ownerUserId === user.id);
  if (своя && tab !== "access" && tab !== "settings") return true;
  return canViewTab(user, TEAM_DEPT_KEY, TEAM_PROFILE_KEY, tab);
}

/** Право, которое НЕ обходится ролью администратора платформы.
 *
 *  ⚠️ ЗАЧЕМ ОТДЕЛЬНАЯ ФУНКЦИЯ. Обычная проверка прав возвращает true любому
 *  platform_admin — так устроено ядро портала, и обычно это правильно. Но есть
 *  вещи, которые нужны ОДНОМУ человеку, а администраторов у нас четверо
 *  (Максим, 04.09.2026: «чтобы только у меня в кабинете отображались эти
 *  блоки»). Здесь смотрим ровно на выданную запись — без роли и без вывода из
 *  соседних вкладок.
 */
export function имеетПравоЯвно(user: User | null, ключ: string, action: PermissionCode = "view"): boolean {
  if (!user) return false;
  const want = foldAction(action);
  return user.permissions.some((p) => p.moduleKey === ключ && p.allowed.includes(want));
}

/** Ключ права на блоки состояния портала в кабинете. */
export const ПРАВО_СОСТОЯНИЕ_ПОРТАЛА = "tab:__system__:portal_ops";

/** Права на пункты меню верхнего уровня. Проверяются `имеетПравоЯвно`, то есть
 *  роль администратора их НЕ обходит: список пунктов должен быть ровно тем, что
 *  выдано в матрице (Максим, 04.09.2026). Страницы при этом остаются доступными
 *  по прямой ссылке — это про навигацию, а не про запрет данных. */
export const ПРАВО_ГЛАВНАЯ = "tab:__system__:page_home";
export const ПРАВО_КАБИНЕТ = "tab:__system__:page_cabinet";
export const ПРАВО_ИНСТРУКЦИИ = "tab:__system__:page_help";
/** Пункт «Команда» — то же именное правило: право на список раздела. */
export const ПРАВО_КОМАНДА = "tab:team:__roster";

/** Есть ли у человека хоть одно ДЕЙСТВУЮЩЕЕ право в отделе.
 *
 *  ⚠️ ОТЛИЧИЕ ОТ deptUsesTabPerms. Та проверяет само НАЛИЧИЕ записи с ключом
 *  отдела — а выключенный в матрице тумблер записи не удаляет, он оставляет её
 *  с пустым списком действий. Из-за этого отдел, где «доступно 0», всё равно
 *  считался доступным и показывался в меню (Максим, 04.09.2026: «но Киямова
 *  видит в меню отделы»). У неё так и было: 18 записей по R&D и все пустые.
 */
export function естьПраваВОтделе(user: User | null, deptKey: string): boolean {
  if (!user) return false;
  const префикс = `tab:${deptKey}:`;
  return user.permissions.some((p) => p.moduleKey.startsWith(префикс) && p.allowed.length > 0);
}

export function clonePermissions(permissions: ModulePermission[]): ModulePermission[] {
  return permissions.map((permission) => ({
    moduleKey: permission.moduleKey,
    allowed: [...permission.allowed],
  }));
}
